import { blankSubCell } from "../factories";
import { NotationContent } from "../model";
import { mapVector } from "./internal";

/**
 * Cell / subcell operations. All pure: `(content, args) => content`.
 *
 * A "subcell" is one swara within a beat. Inserting or removing a subcell
 * applies to EVERY track in that matra, so the tracks stay aligned — that is
 * why these iterate `vector.cells` rather than touching a single cell.
 */

export interface UpdateValueAtCellArgs {
    sectionIndex: number;
    rowIndex: number;
    vectorIndex: number;
    cellIndex: number;
    subCellIndex: number;
    value: string;
    /** File-wide track count; part of the original bounds check. */
    vectorLength: number;
}

export function updateValueAtCell<T extends NotationContent>(
    content: T,
    args: UpdateValueAtCellArgs
): T {
    const {
        sectionIndex,
        rowIndex,
        vectorIndex,
        cellIndex,
        subCellIndex,
        value,
        vectorLength,
    } = args;

    // BUG FIX — this op is now TOTAL: an out-of-range index is a no-op.
    //
    // It used to dereference the whole path *before* validating it, so a bad
    // section or row index threw a TypeError before even reaching the explicit
    // error. Because this runs from a React `onChange`, that took down the
    // editor via the ErrorBoundary — losing unsaved work — in response to a
    // stale index. Every level of the path is resolved safely first.
    const row = content.sections[sectionIndex]?.rows[rowIndex];
    if (!row) return content;

    const cell = row.vectors[vectorIndex]?.cells[cellIndex];
    if (!cell) return content;

    const inRange =
        vectorIndex >= 0 &&
        vectorIndex < row.vectors.length &&
        cellIndex >= 0 &&
        cellIndex < vectorLength + row.options.additionalRowVectorElements &&
        subCellIndex >= 0 &&
        subCellIndex < cell.length;

    if (!inRange) return content;

    return mapVector(content, sectionIndex, rowIndex, vectorIndex, (vector) => ({
        ...vector,
        cells: vector.cells.map((c, i) =>
            i === cellIndex
                ? c.map((sub, j) => (j === subCellIndex ? value : sub))
                : c
        ),
    }));
}

/** Add one subcell to every track of a matra (subdividing the beat). */
export function insertSubCell<T extends NotationContent>(
    content: T,
    args: { sectionIndex: number; rowIndex: number; vectorIndex: number }
): T {
    return mapVector(
        content,
        args.sectionIndex,
        args.rowIndex,
        args.vectorIndex,
        (vector) => ({
            ...vector,
            cells: vector.cells.map((cell) => [...cell, blankSubCell()]),
        })
    );
}

export function removeSubCell<T extends NotationContent>(
    content: T,
    args: {
        sectionIndex: number;
        rowIndex: number;
        vectorIndex: number;
        subCellIndex: number;
    }
): T {
    return mapVector(
        content,
        args.sectionIndex,
        args.rowIndex,
        args.vectorIndex,
        (vector) => {
            if ((vector.cells[0]?.length ?? 0) > 1) {
                return {
                    ...vector,
                    cells: vector.cells.map((cell) => [
                        ...cell.slice(0, args.subCellIndex),
                        ...cell.slice(args.subCellIndex + 1),
                    ]),
                };
            }
            // BUG FIX — clearing the last remaining subcell now works.
            //
            // The original wrote `cell = getBlankCell()` inside a `forEach`,
            // which reassigns the callback parameter and does nothing at all.
            // A beat never drops below one subcell, so the last one is emptied
            // rather than removed.
            return {
                ...vector,
                cells: vector.cells.map((cell) => cell.map(() => "")),
            };
        }
    );
}

/**
 * Rotates every subcell in a matra one step left or right.
 *
 * RENAMED from `moveSubCell`. It never moved an individual subcell: it took a
 * `subCellIndex` and used it only as a guard, then rotated the whole beat. The
 * UI passed hardcoded 1/0 purely to satisfy that guard. The behaviour is
 * unchanged — this is the operation the chevron buttons have always performed —
 * but the name no longer lies about it, and the vestigial index is gone.
 */
export function rotateSubCells<T extends NotationContent>(
    content: T,
    args: {
        sectionIndex: number;
        rowIndex: number;
        vectorIndex: number;
        direction: "left" | "right";
    }
): T {
    const move = args.direction;

    return mapVector(
        content,
        args.sectionIndex,
        args.rowIndex,
        args.vectorIndex,
        (vector) => ({
            ...vector,
            cells: vector.cells.map((cell) => {
                if (cell.length === 0) return cell;
                return move === "left"
                    ? [...cell.slice(1), cell[0]]
                    : [cell[cell.length - 1], ...cell.slice(0, -1)];
            }),
        })
    );
}
